import type { CalculatorResults } from "../engine";
import { fmtAge, fmtMoney, fmtMoneyCents, fmtPct } from "./format";
import type { Household } from "./Form";

/**
 * The full disclaimer block, verbatim from
 * cfg/cmg/tca/tca-content-ops/assets/disclaimers/standard-blog-footer.md.
 * Do not modify without going through that file's modification protocol —
 * the compliance check skill compares published surfaces against the canonical text.
 */
export const DISCLAIMER_VERBATIM = [
  "This article is published by Confluence Media Group LLC, an independent publisher of educational financial content. Thomas Clark is a Series 65 Investment Advisor Representative. The information provided is for educational and informational purposes only and is not personalized financial, tax, or legal advice. Past performance does not guarantee future results. All investing involves risk, including potential loss of principal. Consult a qualified professional before making financial decisions.",
  "Confluence Media Group LLC is a separate entity from Confluence Capital Management, the investment advisory practice through which Thomas Clark provides advisory services. Advisory services are not offered through this publishing platform.",
];

interface JsPdfDoc {
  setFont(name: string, style?: string): void;
  setFontSize(size: number): void;
  text(text: string | string[], x: number, y: number, opts?: object): void;
  setDrawColor(r: number, g: number, b: number): void;
  setLineWidth(w: number): void;
  line(x1: number, y1: number, x2: number, y2: number): void;
  splitTextToSize(text: string, maxWidth: number): string[];
  addPage(): void;
  save(filename: string): void;
  internal: {
    pageSize: { getWidth(): number; getHeight(): number };
  };
}
interface JsPdfCtor {
  new (opts?: { unit?: string; format?: string }): JsPdfDoc;
}

const JSPDF_CDN = "https://unpkg.com/jspdf@2.5.1/dist/jspdf.umd.min.js";

let jspdfPromise: Promise<JsPdfCtor> | null = null;

function loadJsPdf(): Promise<JsPdfCtor> {
  if (jspdfPromise) return jspdfPromise;
  jspdfPromise = new Promise((resolve, reject) => {
    const w = window as unknown as { jspdf?: { jsPDF: JsPdfCtor } };
    if (w.jspdf?.jsPDF) {
      resolve(w.jspdf.jsPDF);
      return;
    }
    const script = document.createElement("script");
    script.src = JSPDF_CDN;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.onload = () => {
      if (w.jspdf?.jsPDF) resolve(w.jspdf.jsPDF);
      else reject(new Error("jsPDF loaded but jspdf.jsPDF not found"));
    };
    script.onerror = () => reject(new Error("Failed to load jsPDF from CDN"));
    document.head.appendChild(script);
  });
  return jspdfPromise;
}

interface GenOpts {
  results: CalculatorResults;
  household: Household;
  colaRate: number;
}

export async function generatePdfReport({ results, household, colaRate }: GenOpts): Promise<void> {
  const JsPDF = await loadJsPdf();
  const doc = new JsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const margin = 54;
  let y = margin;

  const writeHeading = (text: string, size = 16) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(size);
    doc.text(text, margin, y);
    y += size + 6;
  };
  const writeText = (text: string, size = 10) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(text, W - margin * 2);
    doc.text(lines, margin, y);
    y += lines.length * (size + 2) + 4;
  };
  const writeRow = (left: string, right: string, size = 10) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(size);
    doc.text(left, margin, y);
    doc.text(right, W - margin, y, { align: "right" });
    y += size + 4;
  };
  const ensureSpace = (needed: number) => {
    if (y + needed > H - margin) {
      doc.addPage();
      y = margin;
    }
  };
  const hr = () => {
    doc.setDrawColor(200, 195, 185);
    doc.setLineWidth(0.5);
    doc.line(margin, y, W - margin, y);
    y += 10;
  };

  writeHeading("Your Social Security Scenario", 18);
  writeText("Thomas Clark Advisor — Social Security Timing Calculator", 11);
  hr();

  writeText(
    `Based on the assumptions you entered, with a ${fmtPct(colaRate)} annual COLA. ` +
      "These are estimates, not advice. Verify your own PIA on your SSA statement at ssa.gov."
  );

  ensureSpace(140);
  writeHeading("Your monthly benefit by claim age", 13);
  const primaryFraLabel = fmtAge(results.primary.fra.years, results.primary.fra.months);
  writeRow("Claim age", "Monthly      % of PIA      Cumulative through longevity", 9);
  const primary = results.primary;
  const rows = (label: string, claimAge: string, m: number, p: number, c: number) => {
    writeRow(`${label} (${claimAge})`, `${fmtMoneyCents(m)}      ${fmtPct(p)}      ${fmtMoney(c)}`, 10);
  };
  rows("Age 62", "62", primary.benefits.at62.monthlyBenefitInitial, primary.benefits.at62.pctOfPia, primary.benefits.at62.lifetimeCumulative);
  rows("Full Retirement Age", primaryFraLabel, primary.benefits.atFra.monthlyBenefitInitial, primary.benefits.atFra.pctOfPia, primary.benefits.atFra.lifetimeCumulative);
  rows("Age 70", "70", primary.benefits.at70.monthlyBenefitInitial, primary.benefits.at70.pctOfPia, primary.benefits.at70.lifetimeCumulative);
  rows("Custom", String(primary.benefits.custom.claimAge), primary.benefits.custom.monthlyBenefitInitial, primary.benefits.custom.pctOfPia, primary.benefits.custom.lifetimeCumulative);

  if (household === "couple" && results.spouse) {
    ensureSpace(140);
    writeHeading("Spouse monthly benefit by claim age", 13);
    const sp = results.spouse;
    const spFra = fmtAge(sp.fra.years, sp.fra.months);
    writeRow("Claim age", "Monthly      % of PIA      Cumulative through longevity", 9);
    rows("Age 62", "62", sp.benefits.at62.monthlyBenefitInitial, sp.benefits.at62.pctOfPia, sp.benefits.at62.lifetimeCumulative);
    rows("Full Retirement Age", spFra, sp.benefits.atFra.monthlyBenefitInitial, sp.benefits.atFra.pctOfPia, sp.benefits.atFra.lifetimeCumulative);
    rows("Age 70", "70", sp.benefits.at70.monthlyBenefitInitial, sp.benefits.at70.pctOfPia, sp.benefits.at70.lifetimeCumulative);
    rows("Custom", String(sp.benefits.custom.claimAge), sp.benefits.custom.monthlyBenefitInitial, sp.benefits.custom.pctOfPia, sp.benefits.custom.lifetimeCumulative);

    if (results.couple) {
      ensureSpace(90);
      writeHeading("Couples view", 13);
      writeText(`Spousal benefit at FRA (lower earner): ${fmtMoney(results.couple.spousalBenefitAtFra)}.`);
      writeText(`Survivor benefit (higher earner's claim-adjusted monthly): ${fmtMoney(results.couple.survivorBenefit)}.`);
      writeText(results.couple.survivorOptimizationHint);
    }
  }

  ensureSpace(100);
  writeHeading("What this scenario shows", 13);
  writeText(results.recommendation);

  ensureSpace(80);
  hr();
  writeHeading("Disclaimer", 11);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  for (const para of DISCLAIMER_VERBATIM) {
    ensureSpace(40);
    const lines = doc.splitTextToSize(para, W - margin * 2);
    doc.text(lines, margin, y);
    y += lines.length * 11 + 6;
  }

  ensureSpace(20);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(
    `Engine v${results.meta.engineVersion} · FRA verified ${results.meta.fraTableVerified} · ` +
      `Bend points verified ${results.meta.bendPointsVerified} · COLA verified ${results.meta.colaDefaultVerified} · ` +
      `Generated ${new Date().toISOString().slice(0, 10)}`,
    margin,
    H - margin / 2
  );

  doc.save("tca-social-security-scenario.pdf");
}

/**
 * Fallback: invoke the browser print dialog so the user can save-as-PDF.
 * Used when the jsPDF CDN load fails (CSP block, offline, etc.).
 */
export function fallbackPrint(): void {
  window.print();
}
